import { later, next } from '@ember/runloop';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import { addMetricsToHome } from '@databox/ember-integrations/lib/components/Home/addHomeMetricWidgets';
import { AiTrackingOrigin } from '@databox/types/domains/ai/chat.enums';
import type { HomeMetricWidgetSeed } from '@databox/types/domains/home/home';
import { HOME_LIMITS } from '@databox/types/domains/home/home.constants';
import { HomeMetricWidgetsOutcome } from '@databox/types/domains/home/home.enums';

import type {
	APIData,
	ColorMappings,
	CreateRecordProperties,
	DateRangeInterface,
	DSSetup,
	MetricSelectOrigin,
	PopupData,
	PopupListItem,
	RecordData,
	RequestError,
	Service,
} from 'webapp-types';
import type { MetricFilterOperator } from 'webapp/enums/enums';
import {
	BatchSelectActionType,
	BatchSelectObjectType,
	BillingFeature,
	BlockVisualization,
	CompareFunction,
	PopupContextSwitchAction as ContextSwitchAction,
	PopupContextSwitchModelType as ContextSwitchModelType,
	MetricType,
	MetricValueType,
	ModelName,
	PopupEvent,
	RequestFormType,
	Theme,
	Unit,
	VerificationObjectType,
	VerificationStatus,
} from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import {
	compact,
	copyObject,
	generateNormalizedString,
	repeatArrayItems,
	serializeMetricSetupData,
	sortByPopularity,
	stringifyAlphabetical,
	uniq,
} from 'webapp/utils/common';
import { buildHomeSession, toReactMetricSetup } from 'webapp/utils/home';
import { sanitize } from 'webapp/utils/html';
import { getPrettyModelName } from 'webapp/utils/model';
import { hashCode } from 'webapp/utils/number';
import {
	buildMetricCompositeId,
	buildUnverifiedSkeleton,
	getMetricVerificationObjectType,
	type VerificationInfo,
} from 'webapp/utils/verification';

import BlockFiltersPopupModalComponent from 'webapp/components/block/filters/popup-modal';
import CommonPopupSourceMetricRequestFormComponent from 'webapp/components/common/popup-source-metric-request-form';
import type { Option as ColorOption } from 'webapp/components/common/popups/popup-colors';
import CommonPopupsColorsComponent from 'webapp/components/common/popups/popup-colors';
import CustomMetricsEditPopupComponent from 'webapp/components/custom-metrics/edit/popup';
import FormMetricSelectPopupComponent, {
	METRIC_SELECT_POPUP_CLASS,
} from 'webapp/components/form/metric-select/popup';
import type { MetricTabId } from 'webapp/components/metric-library/popups/popup-metric-extra-info';
import MetricLibraryPopupMetricExtraInfoComponent from 'webapp/components/metric-library/popups/popup-metric-extra-info';
import MetricEditPopupComponent from 'webapp/components/metric/edit/popup';
import MetricPushPopupComponent from 'webapp/components/metric/push/popup';
import MetricsUnusedPopupComponent from 'webapp/components/metrics/unused/popup';

import type { BlockRecord, MetricRecord } from 'webapp/models';
import type { CustomQuery, Goal, MetricFromClient, Script } from 'webapp/models-custom';
import Board from 'webapp/models-custom/board';
import DateRange from 'webapp/models-custom/date-range';
import { MetricType as UnusedMetricType } from 'webapp/models-custom/filter-sort/metric-unused';
import type Granulation from 'webapp/models-custom/granulation';
import type HorizonMetric from 'webapp/models-custom/horizon-metric';
import Metric from 'webapp/models-custom/metric';
import type MetricLimitationBase from 'webapp/models-custom/metric-limitation/base';
import MetricLimitations from 'webapp/models-custom/metric-limitations';
import MetricSetup from 'webapp/models-custom/metric-setup';
import type MetricSetupForMasterFilter from 'webapp/models-custom/metric-setup-for-master-filter';

import type { TrackingOriginForCreateOrEdit as TrackingOriginForAlertCreateOrEdit } from 'webapp/services/model-manager/alerts';
import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';
import type { RSPromise } from 'webapp/services/request';

export interface TrackingData extends APIData.Tracking.Data {
	metrics: string;
	datasource: string;
	date_range?: string;
	granulation?: string;
	origin?: string;
	batched?: boolean;
}

export type OnAfterMetricEditFn = (metrics: Metric[]) => void;

export type TrackingOriginForMetricEdit =
	| 'designer'
	| 'listing'
	| 'metrics'
	| 'metric_select'
	| 'allmetrics'
	| 'metrics_detail'
	| MetricSelectOrigin
	| 'wizard'
	| 'datasets'
	| 'missing_features_setup_popup'
	| '';

// For basic metrics, BE only returns the generic versions (which never update during app lifecycle)
// and the actual sessions for each google analytics "metric" models are generated on FE (see updateMetricsFromSources)
// so, don't rely on metric.key being unique (e.g. basics) or use metric.id in communcation with BE (bcos it can be FE-generated)

export default class ModelManagerMetricsService extends ModelManagerForStoreService<
	MetricRecord,
	Metric
> {
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('model-manager/scripts') declare mmScriptsService: Service.ModelManager.Scripts;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('model-manager/horizon-metrics')
	declare mmHorizonMetricsService: Service.ModelManager.HorizonMetrics;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('constants') declare constantsService: Service.Constants;
	@service('logger') declare loggerService: Service.Logger;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/alerts') declare mmAlertsService: Service.ModelManager.Alerts;
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;
	@service('flow/promotions') declare flowPromotionsService: Service.Flow.Promotions;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('verification') declare verificationService: Service.Verification;

	// Favoriting moves onto the metric registry item in M2; until then every favorite action stays hidden.
	readonly showFavoriteAction = false;

	private _allMetricsWithPredefined: Metric[] = [];

	@cached
	private get allMetricsWithPredefined(): Metric[] {
		// all all, with duplicate predefineds and invalid source types
		this.updateModelCache(this._allMetricsWithPredefined, this.savedRecords);
		return this._allMetricsWithPredefined;
	}

	@cached
	private get validMetrics(): Metric[] {
		// for safety, filtering out metrics from source types that don't exist anymore etc.
		return this.allMetricsWithPredefined.filter((m) => !!m.sourceType);
	}

	@cached
	private get validMetricsBySpaceAccessIdAndKey() {
		// performance improvement over matching by spaceAccessId and key directly
		const map = new Map<number | null, Map<string, Metric>>();
		this.validMetrics.forEach((m) => {
			const spaceAccessId = m.spaceAccessId || null;
			if (!map.has(spaceAccessId)) {
				map.set(spaceAccessId, new Map());
			}
			map.get(spaceAccessId)?.set(m.key, m);
		});
		return map;
	}

	@cached
	get metrics(): Metric[] {
		const validMetrics = this.validMetrics;
		// excluding predefined that have already been used
		const usedPredefinedBlocksPerSourceId: Record<number, number[]> = {};
		validMetrics.forEach((m) => {
			const spaceAccessId = m.spaceAccessId;
			if (spaceAccessId && m.isCreatedFromPredefinedMetric) {
				if (!usedPredefinedBlocksPerSourceId[spaceAccessId]) {
					usedPredefinedBlocksPerSourceId[spaceAccessId] = [];
				}
				const predefinedItemId = m.createdFromPredefinedMetric?.objectId;
				if (predefinedItemId) {
					usedPredefinedBlocksPerSourceId[spaceAccessId].push(predefinedItemId);
				}
			}
		});

		return validMetrics.filter((m) => {
			const spaceAccessId = m.spaceAccessId;
			if (spaceAccessId && m.isPredefined) {
				return !(usedPredefinedBlocksPerSourceId[spaceAccessId] || []).includes(
					m.objectId!,
				);
			}
			return true;
		});
	}

	@cached
	get metricsByPopularity() {
		const metrics = this.metrics.filter((m) => !m.isDeprecated);
		return sortByPopularity(metrics);
	}

	@cached
	get metricsByPopularityConnected() {
		// used mostly in recommended sections, these are ready-to-use metrics
		return this.metricsByPopularity.filter((m) => {
			return (
				m.isOnSource &&
				m.dataSourceTypeKey !== 'GoogleAnalytics' &&
				!m.isGenericCustom &&
				!m.source?.isRoot &&
				!m.isUsageDisabled
			);
		});
	}

	@cached
	get genericPredefinedMetrics(): Metric[] {
		// predefined generic scripts & custom queries on the generic sources
		return this.validMetrics.filter((m) => m.isPredefinedOnGenericSource);
	}

	@cached
	get customMetrics() {
		return this.metrics
			.filter((m) => {
				return m.isTypeCustomQueryOrGeneric;
			})
			.filter(this.filterFunction);
	}

	@cached
	get calculatedMetrics() {
		return this.metrics
			.filter((m) => {
				return m.isTypeMetricCalculationOrGeneric;
			})
			.filter(this.filterFunction);
	}

	get hasAnyCustomAndCalculatedMetrics(): boolean {
		return this.customMetrics.length > 0 && this.calculatedMetrics.length > 0;
	}

	private filterFunction(metric: Metric): boolean {
		return (
			metric.dataSourceTypeKey !== 'GoogleAnalytics' &&
			!!metric.isOnSource &&
			!metric.isDeprecated &&
			!metric.source?.isRoot &&
			!metric.isUsageDisabled
		);
	}

	getMetricForKeyAndSpaceAccessId(key: string, spaceAccessId: number | null): Metric | null {
		return this.validMetricsBySpaceAccessIdAndKey.get(spaceAccessId)?.get(key) || null;
	}

	getMetricsForScriptId(scriptId: number): Metric[] {
		return this.validMetrics.filter(
			(m) => m.isTypeMetricCalculation && m.objectId === scriptId,
		);
	}

	getMetricsForQueryId(queryId: number): Metric[] {
		return this.validMetrics.filter((m) => m.isTypeCustomQuery && m.objectId === queryId);
	}

	getMetricsForHorizonMetricId(horizonMetricId: number): Metric[] {
		return this.validMetrics.filter((m) => m.isTypeHorizon && m.objectId === horizonMetricId);
	}

	getGenericMetricsForSourceTypeKey(sourceTypeKey: string): Metric[] {
		// do not include user's generics
		return this.getMetricsForSourceTypeKey(sourceTypeKey).filter(
			(m) => !m.isOnSource && !m.isUserGeneric,
		);
	}

	private getMetricsForSourceTypeKey(sourceTypeKey: string): Metric[] {
		return this.metrics.filter((m) => m.dataSourceTypeKey === sourceTypeKey);
	}

	// Unlike getMetricsForSpaceAccessId, includes used predefined metrics: the set getMetricForKeyAndSpaceAccessId resolves against.
	getValidMetricsForSpaceAccessId(spaceAccessId: number): Metric[] {
		return this.validMetrics.filter((m) => m.spaceAccessId === spaceAccessId);
	}

	getMetricsForSpaceAccessId(spaceAccessId: number): Metric[] {
		return this.metrics.filter((m) => m.spaceAccessId === spaceAccessId);
	}

	getMetricsForConnectedSources(metrics: Metric[], maxItemsShown: number) {
		const connectedSourceTypeKeys = uniq(
			this.mmSourcesService.sourceModels.map((s) => s.sourceType?.key),
		);

		let allMetrics: Metric[] = [];
		if (connectedSourceTypeKeys) {
			allMetrics = metrics.filter((metric) =>
				connectedSourceTypeKeys.includes(metric.dataSourceTypeKey),
			);
		}

		return allMetrics.slice(0, maxItemsShown);
	}

	// these do not change during app lifetime
	private _genericBasicMetricRecordsBySourceType: Record<string, MetricRecord[]> | null = null;

	@cached
	private get genericBasicMetricRecordsBySourceType(): Record<string, MetricRecord[]> {
		const map: Record<string, MetricRecord[]> = {};
		if (!this._genericBasicMetricRecordsBySourceType) {
			this.savedRecords.forEach((metricRecord) => {
				if (metricRecord.isGenericBasic) {
					const key = metricRecord.dataSourceTypeKey;
					if (key) {
						if (!map[key]) {
							map[key] = [];
						}
						map[key].push(metricRecord);
					}
				}
			});
			this._genericBasicMetricRecordsBySourceType = map;
		}
		return this._genericBasicMetricRecordsBySourceType;
	}

	getBasicMetricRecordsForSourceTypeKey(sourceTypeKey: string): MetricRecord[] {
		return this.genericBasicMetricRecordsBySourceType[sourceTypeKey] || [];
	}

	// these do not change during app lifetime
	private _genericPredefinedMetricRecordsBySourceType: Record<string, MetricRecord[]> | null =
		null;

	@cached
	private get genericPredefinedMetricRecordsBySourceType(): Record<string, MetricRecord[]> {
		const map: Record<string, MetricRecord[]> = {};
		if (!this._genericPredefinedMetricRecordsBySourceType) {
			this.savedRecords.forEach((metricRecord) => {
				if (metricRecord.isPredefinedOnGenericSource) {
					const key = metricRecord.dataSourceTypeKey;
					if (key) {
						if (!map[key]) {
							map[key] = [];
						}
						map[key].push(metricRecord);
					}
				}
			});
			this._genericPredefinedMetricRecordsBySourceType = map;
		}
		return this._genericPredefinedMetricRecordsBySourceType;
	}

	getGenericPredefinedMetricRecordsForSourceTypeKey(sourceTypeKey: string): MetricRecord[] {
		return this.genericPredefinedMetricRecordsBySourceType[sourceTypeKey] || [];
	}

	getMetricSetupForMetricId(id: string | null) {
		// used to get setups for uniques (e.g. goals and custom metrics where id === key)
		if (!id) {
			return null;
		}
		const metric = this.findRecordQuick(id);
		if (!metric) {
			return null;
		}
		const metricSetup = new MetricSetup({
			key: metric.key,
			spaceAccessId: metric.spaceAccessId,
		});
		return metricSetup;
	}

	getChangeFormatValuesForChangeFunction(
		changeFunction: CompareFunction,
	): LabelValuePairString[] {
		const changeFormatType =
			changeFunction === CompareFunction.ABSOLUTE ? 'absolute' : 'percent';
		return Array.from(this.constantsService.changeFormats[changeFormatType]);
	}

	getChangeFormatFromChangeFunction(
		changeFunction: CompareFunction,
		currentFormat: string,
	): string {
		const formatValues = this.getChangeFormatValuesForChangeFunction(changeFunction);

		let format = currentFormat || formatValues[0]?.value || '';
		const isAbsolute = changeFunction === CompareFunction.ABSOLUTE;

		if (isAbsolute) {
			if (format.includes('%')) {
				format = format.replace('%', '');
			}
		} else {
			if (!format.includes('%')) {
				format = format + '%';
			}
		}

		return format;
	}

	formatDescription(
		metric: Metric | MetricFromClient | null,
		customDescription?: string,
	): string {
		if (!metric) {
			return '';
		}

		let formattedDescription = `<strong>${sanitize(metric.name)}</strong>`;
		const description = sanitize(customDescription || metric.description);
		if (description) {
			formattedDescription += `<br>${description}`;
		}

		if (this.currentUserService.isPowerUser) {
			formattedDescription += `<br><br><span class="metric-data">key: ${metric.key}<br>spaceAccessId: ${metric.spaceAccessId}</span>`;
		}

		return formattedDescription;
	}

	editMetric(
		metric: Metric,
		origin: TrackingOriginForMetricEdit = '',
		afterSave?: OnAfterMetricEditFn,
		extraProps: {
			isOpenedFromQueryOrScriptForm?: boolean;
			isPeriodDataTypeHidden?: boolean;
			isAllValuesDataTypeHidden?: boolean;
			openMetricDefinitionPopupOnInit?: boolean;
		} = {},
	) {
		if (metric.isTypeBasic || metric.isTypeCustomPush) {
			this.openMetricDefinitionPopup(
				metric,
				extraProps.isOpenedFromQueryOrScriptForm,
				extraProps.isPeriodDataTypeHidden,
				extraProps.isAllValuesDataTypeHidden,
				afterSave,
			);
		} else if (metric.isTypeGoal) {
			const goal = metric.rootModel as Goal;
			if (goal) {
				this.mmGoalsService.openEditPopup(goal, origin, () => {
					if (afterSave) {
						afterSave([metric]);
					}
				});
			}
		} else {
			const rootModel = metric.rootModel;
			if (rootModel) {
				if (metric.isTypeMetricCalculation) {
					metric.mmScriptsService.openEditPopup(
						rootModel as Script,
						origin,
						(model) => {
							if (afterSave) {
								const script = model as Script;
								if (script) {
									const metrics = this.getMetricsForScriptId(
										script.numericIdForSure,
									);
									afterSave(metrics);
								}
							}
						},
						extraProps.openMetricDefinitionPopupOnInit,
					);
				} else if (metric.isTypeCustomQuery) {
					this.mmCustomQueriesService.openEditPopup(
						rootModel as CustomQuery,
						origin,
						extraProps.openMetricDefinitionPopupOnInit,
						(model) => {
							if (afterSave) {
								const customQuery = model as CustomQuery;
								if (customQuery) {
									const metrics = this.getMetricsForQueryId(
										customQuery.numericIdForSure,
									);
									afterSave(metrics);
								}
							}
						},
					);
				} else if (metric.isTypeHorizon) {
					this.mmHorizonMetricsService.openEditPopup(
						rootModel as HorizonMetric,
						origin,
						afterSave
							? () => {
									const horizonMetric = rootModel as HorizonMetric;
									if (horizonMetric.numericId) {
										const metrics = this.getMetricsForHorizonMetricId(
											horizonMetric.numericId,
										);
										afterSave(metrics);
									}
								}
							: undefined,
					);
				}
			}
		}
	}

	createCustomMetricEditPopup() {
		const popup = this.popupService.createModal(CustomMetricsEditPopupComponent, {
			popupClass: 'popup-custom-metric-edit',
			allowStacked: true,
			allowMultipleInstances: true, // when duplicating, we open new one before we close the one below
		});
		return popup;
	}

	openCreateNewCustomMetricPopup(
		origin: TrackingOriginForMetricEdit,
		afterSave?: PopupData.CustomMetric.Edit['afterSave'],
	) {
		if (this.accountService.handleDemo()) {
			return;
		}

		if (!this.billingService.scriptsEnabled && !this.billingService.qbEnabled) {
			this.billingService.handleLimitations(BillingFeature.QB);
			return;
		}

		if (!this.billingService.checkIfCan(BillingFeature.CUSTOM_METRICS, true)) {
			return;
		}

		const popup = this.createCustomMetricEditPopup();

		popup.open({
			origin,
			afterSave,
		});

		return popup;
	}

	createNewCustomPushMetric(spaceAccessId: number) {
		const metric = this.createNewModel({
			spaceAccessId,
			objectType: MetricType.CUSTOM_PUSH,
		});
		metric.edit();
	}

	openMetricSelectPopup(
		popupProps: {
			[Property in keyof PopupData.Form.MetricSelect]?: PopupData.Form.MetricSelect[Property];
		} = {},
		theme?: Theme,
	): Promise<DSSetup.MetricSetupData> {
		const mainProps = {
			popupClass: METRIC_SELECT_POPUP_CLASS,
			allowStacked: true,
			allowMultipleInstances: true,
			theme,
		};

		return new Promise((resolve, reject) => {
			const popup = this.popupService.createModal(FormMetricSelectPopupComponent, mainProps);

			const props: PopupData.Form.MetricSelect = Object.assign(
				{
					metric: null,
					origin: 'designer',
					disableNotConnected: true,
					onChange: (metricSetupData: DSSetup.MetricSetupData) => {
						resolve(metricSetupData);
					},
				},
				popupProps,
			);
			popup.open(props);

			popup.on(PopupEvent.CLOSED, this, () => {
				reject();
			});
		});
	}

	openMetricDefinitionPopup(
		metric: Metric,
		isOpenedFromQueryOrScriptForm = false,
		isPeriodDataTypeHidden = false,
		isAllValuesDataTypeHidden = false,
		afterSave?: OnAfterMetricEditFn,
		onSave?: (metricData: RecordData.Metric.Serialized) => void,
	) {
		const popup = this.popupService.createModal(MetricEditPopupComponent, {
			allowStacked: true, // to open custom format popup
			popupClass: 'popup-type-edit-metric',
		});
		const metricCopy = this.createModelFromRecord(metric.model);
		popup.open({
			model: metricCopy,
			isOpenedFromQueryOrScriptForm,
			isPeriodDataTypeHidden,
			isAllValuesDataTypeHidden,
			afterSave,
			onSave,
		});

		return popup;
	}

	getAlreadyTakenMetricIds(spaceAccessId: number | null | undefined): string[] {
		if (spaceAccessId) {
			const metricIds: string[] = [];
			this.getMetricsForSpaceAccessId(spaceAccessId).forEach((m) => {
				const metricId = m.metricId;
				if (metricId) {
					metricIds.push(metricId);
				}
			});
			return metricIds;
		} else {
			return [];
		}
	}

	openUnusedMetricCleanupPopup(initialMetricType?: UnusedMetricType): Promise<void> {
		return new Promise((resolve) => {
			const popup = this.popupService.createModal(MetricsUnusedPopupComponent, {
				popupClass: 'popup-type-metrics-unused',
				showClose: true,
				allowStacked: true,
			});
			popup.open({ initialMetricType });
			popup.on(PopupEvent.CLOSING, this, () => {
				resolve();
			});
		});
	}

	requestDataRefetch(payload: APIData.Board.DataRefetch): Promise<{ success: boolean }> {
		return this.requestService.api('POST', '/metrics/re-sync', payload);
	}

	// A generic custom metric is created first (it has no data of its own), then added like any other.
	addMetricsToHome(metrics: Metric[]): Promise<void> {
		if (this.accountService.handleDemo() || !this.currentUserService.hasHome) {
			return Promise.resolve();
		}

		const resolved = metrics
			.filter((metric) => metric.spaceAccessId)
			.map((metric) =>
				this.resolveGenericMetricWithSpecific(metric.key, metric.spaceAccessId!),
			);

		return Promise.all(resolved).then((setupsData) =>
			this.addSeedsToHome(setupsData.map((data) => new MetricSetup(data))),
		);
	}

	addMetricSetupsToHome(metricSetups: MetricSetup[]): Promise<void> {
		if (this.accountService.handleDemo() || !this.currentUserService.hasHome) {
			return Promise.resolve();
		}

		return this.addSeedsToHome(metricSetups);
	}

	private addSeedsToHome(metricSetups: MetricSetup[]): Promise<void> {
		const seeds: HomeMetricWidgetSeed[] = [];
		metricSetups.forEach((metricSetup) => {
			const data = serializeMetricSetupData(metricSetup.metricSetupData);
			const reactMetricSetup = data ? toReactMetricSetup(data) : null;
			if (reactMetricSetup) {
				seeds.push({
					metricSetup: reactMetricSetup,
					title: metricSetup.metric?.name ?? null,
				});
			}
		});

		const count = seeds.length;
		if (!count) {
			return Promise.resolve();
		}

		const maxCount = HOME_LIMITS.MAX_METRIC_WIDGETS;
		if (count > maxCount) {
			this.infolayerService.error(`You can add up to ${maxCount} metrics to Home at once.`);
			return Promise.resolve();
		}

		const metricsText = count === 1 ? 'the metric' : `the ${count} metrics`;
		const failedText = `Couldn't add ${metricsText} to Home. Please try again.`;

		const session = buildHomeSession(
			this.currentUserService,
			this.accountService,
			AiTrackingOrigin.METRICS,
		);
		if (!session) {
			this.infolayerService.error(failedText);
			return Promise.resolve();
		}

		const infolayer = this.infolayerService.loading('Adding to Home...');

		return addMetricsToHome(session, seeds)
			.then((outcome) => {
				if (outcome === HomeMetricWidgetsOutcome.ADDED) {
					infolayer.ok(`Added ${count} ${count === 1 ? 'metric' : 'metrics'} to Home.`);
				} else if (outcome === HomeMetricWidgetsOutcome.LIMIT_EXCEEDED) {
					infolayer.error(
						count === 1
							? "There's no room on Home for another widget."
							: `There's no room on Home for ${count} more widgets.`,
					);
				} else {
					infolayer.error(failedText);
				}
			})
			.catch(() => {
				infolayer.error(failedText);
			});
	}

	batchCreateBlocksFromMetrics(metrics: Metric[], boardId: number): Promise<unknown> {
		const board = this.mmBoardsService.findModel(String(boardId));

		if (!board) {
			return Promise.resolve();
		}

		// maps metric to json data, suitable to /blocks/batch endpoint
		const blocksData: RecordData.Block.Serialized[] = [];

		const promise = Promise.allSettled(
			metrics.map(async (metric, index) => {
				if (!metric.spaceAccessId) {
					return;
				}

				const metricData = await this.resolveGenericMetricWithSpecific(
					metric.key,
					metric.spaceAccessId,
				);
				const metricSetup = new MetricSetup(metricData),
					metricSetupData = metricSetup.metricSetupWithoutFiltersData;

				if (!metricSetupData) {
					return;
				}

				const properties: CreateRecordProperties<BlockRecord> = {
					boardId,
					order: board.blocks.length + index,
					name: metric.name,
					fromMetricSetup: metricSetupData,
					visualization: BlockVisualization.BAR_CHART,
				};

				if (!this.mmBlocksService.isVisualizationSeries(properties.visualization!)) {
					// granulation only, the range comes from the metric
					properties.fromMetricDateRange = {
						granulation: { isWholeRange: true },
					} as DateRangeInterface.FormulaInterface;
				}

				const blockCM = this.mmBlocksService.createNewModel(properties),
					json = blockCM.toSerializedJSON();

				blocksData.push(json);

				void blockCM.delete(true);
			}),
		);

		void promise.then(() => {
			return this.mmBlocksService.bulkCreate(blocksData, board, false);
		});

		return promise;
	}

	// Picks a dashboard, adds a block per metric and offers to switch to the dashboard. Resolves once
	// done, or at once when the plan allows no more dashboards or the pick is cancelled.
	addMetricsToBoard(metrics: Metric[]): Promise<void> {
		if (!this.billingService.checkIfCan(BillingFeature.BOARDS, true)) {
			return Promise.resolve();
		}

		return this.mmBoardsService
			.showBoardChoice(metrics)
			.then(
				(boardId) =>
					new Promise<void>((resolve) => {
						next(() => {
							const layer = this.infolayerService.loading(
								'Adding metrics to dashboard...',
							);

							this.batchCreateBlocksFromMetrics(metrics, boardId)
								.then(() => {
									this.navigationService.createContextSwitchModal().open({
										actionType: ContextSwitchAction.ADD_TO,
										sourceItem: ContextSwitchModelType.BLOCK,
										targetItem: this.mmBoardsService.findModel(String(boardId)),
										onSwitch: () => {
											this.navigationService.replaceRoute(
												'designer.board',
												boardId,
											);
										},
									});

									layer.ok('Added metrics to dashboard!');
								})
								.catch(() => {
									layer.close();
								})
								.finally(resolve);
						});
					}),
			)
			.catch(() => {
				// the dashboard picker closed without a pick
			});
	}

	// For the Metrics page: objectIds are metric registry ids, not metric ids, and a tags update's
	// data is `{ add, remove }`.
	batchMetricRegistryAction(
		operationType: BatchSelectActionType,
		objectIds: number[],
		data?: object,
	): Promise<void> {
		return this.batchAction(operationType, objectIds, data, {
			objectType: BatchSelectObjectType.METRIC_REGISTRY_ITEM,
			objectText: 'metric',
		});
	}

	getRecommendedMetrics(metrics: Metric[], maxCount: number, showSingleSourcePerType = false) {
		const singleConnectedSourcePerType: Record<string, number | ''> = {}; // by source type key, '' when not connected
		const { sourcesSelection, cloudSourcesOpportunity } = this.accountService.metadata;
		const firstConnectedSourceTypeKey =
			this.mmSourcesService.firstConnectedCloudSourceType?.key;

		let model = showSingleSourcePerType
			? metrics.filter((metric) => {
					const sourceType = metric.sourceType;
					if (singleConnectedSourcePerType[sourceType.key] === undefined) {
						singleConnectedSourcePerType[sourceType.key] = sourceType.isAlreadyConnected
							? Number(sourceType.connectedSources[0]!.id)
							: '';
					}

					return sourceType.isAlreadyConnected
						? metric.spaceAccessId === singleConnectedSourcePerType[sourceType.key]
						: true;
				})
			: metrics;

		model = model.filter((metric) => !metric.isDeprecated);

		let preformattedModel: Metric[] = [];
		let formattedModel: Metric[] = [];

		const findRelavantMetricsBySourceTypes = (sourceTypes: string[]) => {
			const filteredSourceTypes = sourceTypes.filter(
				(sourceType) =>
					firstConnectedSourceTypeKey && sourceType !== firstConnectedSourceTypeKey,
			);
			const metricsFromFirstConnectedSourceCount = preformattedModel.length;
			const formattedSourceTypes = repeatArrayItems(
				filteredSourceTypes,
				maxCount - metricsFromFirstConnectedSourceCount,
			);

			let currentSourceType: string | null = null;
			let currentSourceTypeIndex = 0;
			let filteredMetrics: Metric[] = [];
			formattedSourceTypes.sort().forEach((sourceType) => {
				if (currentSourceType !== sourceType) {
					filteredMetrics = model.filter((item) => item.dataSourceTypeKey === sourceType);
					currentSourceType = sourceType;
					currentSourceTypeIndex = 0;
				}

				const metric = filteredMetrics[currentSourceTypeIndex];
				if (metric) {
					formattedModel.push(metric);
				}
				currentSourceTypeIndex++;
			});
		};

		if (firstConnectedSourceTypeKey) {
			preformattedModel = model
				.filter((metric) => metric.dataSourceTypeKey === firstConnectedSourceTypeKey)
				.slice(0, 9); // The first not used 9 metrics are from the first connected cloud source type
		}

		if (sourcesSelection.length) {
			findRelavantMetricsBySourceTypes(sourcesSelection);
		} else if (cloudSourcesOpportunity.length) {
			findRelavantMetricsBySourceTypes(cloudSourcesOpportunity);
		} else {
			formattedModel = model;
		}

		formattedModel = [...preformattedModel, ...formattedModel];

		if (formattedModel.length < maxCount) {
			// add from the leftovers (skip duplicates)
			model.slice(0, maxCount).forEach((metric) => {
				if (!formattedModel.find((m) => m === metric)) {
					formattedModel.push(metric);
				}
			});
		}

		return formattedModel.slice(0, maxCount);
	}

	createGoal(metric: Metric, origin: string) {
		if (this.accountService.handleDemo()) {
			return;
		}

		if (!metric.spaceAccessId) {
			return;
		}

		const metricSetup = new MetricSetup(metric.metricData!);
		this.mmGoalsService.openNewPopupForMetricSetup(metricSetup, origin);
	}

	createAlert(metric: Metric, origin: TrackingOriginForAlertCreateOrEdit) {
		if (this.accountService.handleDemo()) {
			return;
		}

		if (!metric.spaceAccessId) {
			return;
		}

		if (this.billingService.checkIfCan(BillingFeature.ALERTS, true)) {
			const metricSetup = new MetricSetup(metric.metricData!);
			this.mmAlertsService.createAndEditForMetricSetup(metricSetup, origin);
		}
	}

	duplicateMetric(metric?: Metric | null) {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		if (metric) {
			const metric2 = this.createNewModel(metric.getDataForCopy());
			return this.openMetricDefinitionPopup(metric2);
		}
	}

	pushMetricData(metric?: Metric | null) {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		if (!this.billingService.checkIfCan(BillingFeature.DATA_PUSH, true)) {
			return;
		}

		if (!metric) {
			return;
		}

		const popup = this.popupService.createModal(MetricPushPopupComponent, {
			popupClass: 'popup-type-metric-push',
			allowStacked: true, // for error response popup
		});

		popup.open({ model: metric });
	}

	purgeMetricData(metric?: Metric | null) {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		if (!metric) {
			return;
		}

		const affirmativeFunc = () => {
			const layer = this.infolayerService.loading('Purging data...');

			// the metric setup identifies the metric on its source; the backend purges every data
			// configuration of it. We send an empty `dimensions` array and omit filters to purge all variants.
			const payload: DSSetup.MetricSetupData[] = compact([metric.metricData]);

			this.requestService
				.api('POST', '/metrics/purge-data', payload)
				.then(() => {
					layer.ok('Data purged!');
				})
				.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
		};

		this.popupService.openConfirmPopup({
			onConfirm: affirmativeFunc,
			title: `Do you want to purge data for metric "${metric.name}"?`,
			text: 'This cannot be undone.',
			confirmAction: 'Purge',
			cancelAction: 'Cancel',
		});
	}

	deleteMetric(metric?: Metric | null, onSuccess?: () => void) {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		if (!metric) {
			return;
		}

		const modelToDelete = metric.rootModel || metric;
		const modelText = getPrettyModelName(modelToDelete);

		const onConfirm = () => {
			const layer = this.infolayerService.loading('Deleting metric...');

			let preRequest: Promise<unknown>;
			if (modelToDelete instanceof Metric) {
				// using key in request bcos we can delete only custom push or generic metrics where key is unique
				preRequest = this.requestService.api('DELETE', `/metrics/${metric.key}`);
			} else {
				preRequest = modelToDelete.delete();
			}

			preRequest
				.then(
					() => {
						layer.ok('Metric deleted!');
						if (onSuccess) {
							onSuccess();
						}
					},
					(errorData) => {
						layer.close();
						throw errorData;
					},
				)
				.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
		};

		this.popupService.openConfirmPopup({
			onConfirm,
			title: `Do you want to delete this ${modelText}?`,
			text: 'This cannot be undone.',
			confirmAction: 'Delete',
			cancelAction: 'Cancel',
		});
	}

	getBatchActionOptions(selectedCount: number, addCountText = true) {
		const boardsOption: PopupListItem<string> = {
			icon: 'Databoards',
			text: 'Add to dashboard',
			action: 'addSelectedToBoard',
		};

		boardsOption.isDisabled = selectedCount > Board.maxBlocks;
		if (addCountText) {
			boardsOption.text += ` (${selectedCount})`;
		}

		const options = [boardsOption];

		if (this.showFavoriteAction) {
			options.push({
				class: 'star',
				icon: 'Star',
				text: 'Add to favorites',
				action: 'addSelectedToFavorites',
			});
		}

		return options;
	}

	openMetricRequestForm() {
		const popup = this.popupService.createModal(CommonPopupSourceMetricRequestFormComponent, {
			popupClass: 'popup-type-source-metric-request-form',
		});

		popup.open({
			type: RequestFormType.METRICS,
		});

		return popup;
	}

	trackMetricDataEvent(
		event: TrackingEvent,
		metric: Metric | null,
		dateRange?: DateRange,
		origin?: string,
		batched?: boolean,
	) {
		if (metric) {
			const trackingData: TrackingData = {
				metrics: metric.name,
				datasource: metric.dataSourceTypeKey,
			};

			if (dateRange) {
				if (event === TrackingEvent.GRANULATION_CHANGED) {
					trackingData.granulation = dateRange.granulationTitleUX.toLowerCase();
				} else {
					trackingData.date_range = dateRange.normalizedTitleUX;
				}
			}

			if (origin) {
				trackingData.origin = origin;
			}

			if (batched) {
				trackingData.batched = batched;
			}

			this.analyticsService.event(event, trackingData);
		}
	}

	getKeyParts(key: string): string[] {
		// ONLY use for cross-feature metric -> metrickey logic
		if (!key) {
			return [];
		}
		const splitBy = key.includes('@') ? '@' : '|',
			metricParts = key.split(splitBy);
		return [metricParts[0]!, metricParts.slice(1).join(splitBy)];
	}

	// The canonical `${spaceAccessId}|${key}` a lineage node addresses a metric by; the key is the
	// part after the data source type (`GoogleAnalytics4@totalUsers` -> `totalUsers`).
	getLineageKey(key: string, spaceAccessId: number | null): string {
		if (!spaceAccessId) {
			return key;
		}

		const [prefix, postfix] = this.getKeyParts(key);
		return `${spaceAccessId}|${postfix || prefix}`;
	}

	getDefaultsForMetric(metric?: Metric | null) {
		return {
			format: metric?.format || this.constantsService.defaultFormat,
			changeFormat: metric?.changeFormat || this.constantsService.defaultChangeFormat,
			changeFunction: metric?.changeFunction || this.constantsService.defaultChangeFunction,
		};
	}

	isMetricCurrent(metric: Metric) {
		return metric.metricType === MetricValueType.CURRENT;
	}

	openFiltersPopup(props: PopupData.Block.Filters, theme?: string) {
		const popup = this.popupService.createModal(BlockFiltersPopupModalComponent, {
			theme,
			popupClass: 'popup-type-block-filters',
			allowStacked: true,
		});

		popup.open(props);

		return popup;
	}

	openExtraInfoPopup(metric: Metric, initialTab?: MetricTabId) {
		this.popupService
			.createModal(MetricLibraryPopupMetricExtraInfoComponent, {
				popupClass: 'popup-type-metric-extra-info',
			})
			.open({
				model: metric,
				initialTab,
			});
	}

	getLimitationInfoForDateRangeOrGranulation(
		model: DateRange | Granulation | MetricLimitationBase,
		isUserAdmin: boolean,
	): { text: string | JQuery; icon: IconName; className: string } {
		// model is either a DateRange or Granulation
		// but can also be used on Limitation model
		let text: string | JQuery = model.infoText,
			icon: IconName = 'Question',
			className = '';

		if (model.isDisabledForPaymentReasons) {
			// the native app shows no upgrade offers
			if (isUserAdmin && !this.navigationService.isMobileApp) {
				const billingFeature = BillingFeature.CUSTOM_RANGE,
					planForFeature = this.billingService.getPlanForTrial(billingFeature),
					availableTrialPromotion = planForFeature
						? this.flowPromotionsService.getAvailableTrialPromotion(planForFeature)
						: undefined,
					canRequestTrial =
						this.billingService.canRequestTrial || !!availableTrialPromotion,
					innerText = `${text}<br><a>${canRequestTrial ? 'Try 14 days for free' : 'More about pricing & plans &gt;'}</a>`;

				text = $(`<p>${innerText}</p>`);
				text.find('a').on('click', () => {
					if (this.navigationService.isMainApp) {
						if (canRequestTrial) {
							this.billingService.handleLimitations(billingFeature);
						} else {
							this.navigationService.transitionToRoute('settings.billing.plans');
						}
					} else {
						window.open('/account/billing/plans', '_blank');
					}
					return false;
				});
			}

			icon = 'Lock';
			className = 'locked';
		}

		return { text, icon, className };
	}

	// ----------- attribute color picker helpers ------------

	getHexColorForAttribute(
		attribute: string,
		colorMappings: ColorMappings,
		colorOptions: ColorOption[],
	) {
		const indexOrHex = colorMappings[attribute];
		let hex = '';
		if (typeof indexOrHex === 'number') {
			hex = colorOptions.find((o) => o.id === indexOrHex)?.hex || '';
		} else if (indexOrHex) {
			hex = indexOrHex;
		}
		return hex;
	}

	openColorPickerForAttribute(
		attachTo: HTMLElement,
		attribute: string,
		colorMappings: ColorMappings,
		colorOptions: ColorOption[],
		changeColor: (color: string | number | null) => void,
	) {
		const openPopupHere = this.popupService.findContextByComponentAndAttachTo(
			CommonPopupsColorsComponent,
			attachTo,
		);

		if (openPopupHere) {
			// have one open right here, must close
			void openPopupHere.close();
			return;
		} else {
			const openPopup = this.popupService.findByComponent(CommonPopupsColorsComponent);
			void openPopup?.close();
		}

		const popup = this.popupService.createContext(CommonPopupsColorsComponent, attachTo, {
			offset: 0,
			popupClass: 'popup-type-color popup-type-color--block',
			theme: 'dark',
			allowStacked: true,
		});

		const selectedColor = colorMappings[attribute]!;

		// get used colors (except current) for disabling options
		let usedColors = Object.values(colorMappings);
		if (typeof selectedColor === 'number') {
			usedColors = usedColors.filter((c) => c !== selectedColor);
		}
		// copy, bcos we are changing the disabled option
		const colorOptionsCopy: ColorOption[] = copyObject(colorOptions);
		colorOptionsCopy.forEach((o: ColorOption) => {
			if (usedColors.includes(o.id)) {
				o.disabled = true;
			}
		});

		popup.open({
			selected: selectedColor,
			allowEmpty: true,
			emptyText: 'Auto',
			allowCustom: true,
			changeColor,
			allColors: colorOptionsCopy,
		});
	}

	// -------- metric id generate (push) -------

	validateMetricId(metricId: string, isAttributed = false): boolean {
		let isValid = true;

		const metricIdParts = metricId.split('|');

		if (
			(isAttributed && metricIdParts.length !== 2) ||
			(!isAttributed && metricIdParts.length > 1)
		) {
			isValid = false;
		} else {
			metricIdParts.forEach((part) => {
				const normalizedPart = generateNormalizedString(part);
				if (normalizedPart !== part) {
					isValid = false;
				}
			});
		}

		return isValid;
	}

	generateUniqueMetricId(name: string, usedMetricIds: string[] = []): string {
		// generates unique string from metric name

		// default
		let normalizedName = 'metric';

		if (name) {
			normalizedName = generateNormalizedString(name);
		}

		// make sure it's unique
		let counter = 1,
			uniqueNormalizedName = normalizedName;
		while (usedMetricIds.includes(uniqueNormalizedName)) {
			uniqueNormalizedName = `${normalizedName}_${counter}`;
			counter++;
		}

		return uniqueNormalizedName;
	}

	// --- Keeping metric models in sync with connected sources ---

	private _previousValidSpaceAccessIds: number[] = [];

	updateMetricsFromSources(fromMetricsRequestCallback = false) {
		if (!fromMetricsRequestCallback && !this._metricsRequested) {
			return;
		}

		const validSources = this.mmSourcesService.sourceModels,
			validSpaceAccessIds = validSources.map((s) => s.numericId!),
			previousValidSpaceAccessIds = this._previousValidSpaceAccessIds;

		const sourcesAdded = validSources.filter(
				(source) => !previousValidSpaceAccessIds.includes(source.numericId!),
			),
			saidRemoved = previousValidSpaceAccessIds.filter(
				(id) => !validSpaceAccessIds.includes(id),
			);

		if (sourcesAdded.length || saidRemoved.length) {
			// local copies of the sources' metrics (with fake ids), and the removed sources' metrics
			const payload: {
				metrics?: RecordData.Metric.Serialized[];
				deletedRecords?: { metrics: string[] };
			} = {};

			sourcesAdded.forEach((source) => {
				if (source.isTypeOfDataset) {
					return;
				}

				const genericMetricRecords = source.sourceType?.basicMetricRecords || [],
					predefinedMetricRecords = source.sourceType?.predefinedMetricRecords || [],
					metricRecords = [...genericMetricRecords, ...predefinedMetricRecords];

				// create actual copies for specific basic and predefined metrics
				if (metricRecords.length) {
					const spaceAccessId = source.numericId!;
					if (!payload.metrics) {
						payload.metrics = [];
					}
					payload.metrics.push(
						...metricRecords.map((m) => {
							const metricData = m.serialize() as RecordData.Metric.Serialized;
							metricData.id = `${spaceAccessId}-|-${m.key}`; // anything unique goes
							metricData.spaceAccessId = spaceAccessId;
							return metricData;
						}),
					);
				}
			});

			saidRemoved.forEach((spaceAccessId) => {
				const metricRecordsToDelete = this.savedRecords.filter(
					(m) => m.spaceAccessId === spaceAccessId,
				);
				if (metricRecordsToDelete.length) {
					if (!payload.deletedRecords) {
						payload.deletedRecords = {
							metrics: [],
						};
					}
					payload.deletedRecords.metrics.push(...metricRecordsToDelete.map((m) => m.id!));
				}
			});

			this.storeService.pushPayload(payload);

			this._previousValidSpaceAccessIds = validSpaceAccessIds;
		}
	}

	// ------ Metric Verifications ------

	/**
	 * Async: fetches all verified metric verifications for the space and
	 * sets verificationInfo on the corresponding metric records.
	 */
	private async fetchAndUpgradeMetricVerifications(): Promise<void> {
		const objectTypes = [
			VerificationObjectType.BASIC_METRIC,
			VerificationObjectType.CUSTOM_QUERY,
			VerificationObjectType.CALCULATED_METRIC,
		];

		const metricsWithSpaceAccess = this.savedRecords.filter((m) => m.spaceAccessId);
		if (!metricsWithSpaceAccess.length) {
			return;
		}

		const compositeIdToMetricId = new Map<string, string>();
		for (const m of metricsWithSpaceAccess) {
			const compositeId = buildMetricCompositeId(m.key, m.spaceAccessId!);
			if (m.id) {
				compositeIdToMetricId.set(compositeId, m.id);
			}
		}

		for (const objectType of objectTypes) {
			const reply =
				await this.requestService.api<APIData.Verification.ObjectVerificationsResponse>(
					'GET',
					`/object-verifications?objectType=${objectType}`,
				);

			if (!reply?.verifications?.length) {
				continue;
			}

			const payload: { metrics: { id: string; verificationInfo: VerificationInfo }[] } = {
				metrics: [],
			};
			for (const serverInfo of reply.verifications) {
				const metricId = compositeIdToMetricId.get(serverInfo.objectId);
				if (metricId) {
					payload.metrics.push({
						id: metricId,
						verificationInfo: serverInfo,
					});
				}
			}

			if (payload.metrics.length) {
				this.storeService.pushPayload(payload);
			}
		}
	}

	private getOrBuildVerificationInfo(metric: Metric): VerificationInfo | undefined {
		if (metric.verificationInfo) {
			return metric.verificationInfo;
		}
		if (!metric.key || !metric.spaceAccessId) {
			return undefined;
		}
		return buildUnverifiedSkeleton(
			buildMetricCompositeId(metric.key, metric.spaceAccessId),
			getMetricVerificationObjectType(metric.objectType),
		);
	}

	async toggleVerification(metric: Metric): Promise<void> {
		const verificationInfo = this.getOrBuildVerificationInfo(metric);
		if (!verificationInfo) return;
		await this.verificationService.toggleVerification(
			verificationInfo,
			'metric',
			metric.name,
			(updated) => {
				metric.directPropUpdate({ verificationInfo: updated });
			},
		);

		if (metric.isTypeMetricCalculation) {
			void this.fetchAndUpgradeMetricVerifications().catch(() => undefined);
		}
	}

	// Writes the opposite of the given state, so the result follows the caller's intent, not this store's copy.
	async setVerification(
		metric: Metric,
		isCurrentlyVerified: boolean,
	): Promise<boolean | undefined> {
		const previous = metric.verificationInfo;
		const stored = this.getOrBuildVerificationInfo(metric);
		if (!stored) return undefined;

		const info = {
			...stored,
			status: isCurrentlyVerified
				? VerificationStatus.VERIFIED
				: VerificationStatus.UNVERIFIED,
		};
		const savedStatus = await this.verificationService.toggleVerification(
			info,
			'metric',
			metric.name,
			(updated) => {
				metric.directPropUpdate({ verificationInfo: updated });
			},
		);

		// A failed save restores what the store held, which may be nothing.
		if (savedStatus === null) {
			metric.directPropUpdate({ verificationInfo: previous });
		}
		if (metric.isTypeMetricCalculation) {
			void this.fetchAndUpgradeMetricVerifications().catch(() => undefined);
		}

		return savedStatus === null
			? isCurrentlyVerified
			: savedStatus === VerificationStatus.VERIFIED;
	}

	// ------ Correlated Metrics ------
	private _correlatedMetricsRequested: Record<string, DSSetup.MetricSetupWithoutFiltersData[]> =
		{};
	private _correlatedMetricsRequestPromise: Record<
		string,
		Promise<DSSetup.MetricSetupWithoutFiltersData[]>
	> = {};

	getCorrelatedMetrics(
		metricSetupData: DSSetup.MetricSetupWithoutFiltersData,
		limit = 3,
	): Promise<DSSetup.MetricSetupWithoutFiltersData[]> {
		let promise: Promise<DSSetup.MetricSetupWithoutFiltersData[]>;

		const cacheString = JSON.stringify(metricSetupData);

		if (this._correlatedMetricsRequested[cacheString]) {
			promise = Promise.resolve(this._correlatedMetricsRequested[cacheString]);
		} else if (typeof this._correlatedMetricsRequestPromise[cacheString] !== 'undefined') {
			promise = this._correlatedMetricsRequestPromise[cacheString];
		} else {
			promise = new Promise((resolve, reject) => {
				const invalidateIn = 5 * 60 * 1000;

				this.requestService
					.api<DSSetup.MetricSetupWithoutFiltersData[]>(
						'POST',
						'/metric-insights/metric-correlations',
						{
							metricSetup: metricSetupData,
							limit,
						},
					)
					.then(
						(reply) => {
							this._correlatedMetricsRequested[cacheString] = reply;
							resolve(reply);
							later(() => {
								this.cleanupCorrelatedMetricsCacheForString(cacheString);
							}, invalidateIn);
						},
						(errorData) => {
							reject(errorData);
						},
					)
					.catch(() => {
						/* skip */
					})
					.finally(() => {
						delete this._correlatedMetricsRequestPromise[cacheString];
					});
			});

			this._correlatedMetricsRequestPromise[cacheString] = promise;
		}

		return promise;
	}

	private cleanupCorrelatedMetricsCacheForString(cacheString: string): void {
		delete this._correlatedMetricsRequested[cacheString];
	}

	// ------ Resolving generics ------

	private _genericCustomResolveRequestMap: Record<
		string,
		Promise<DSSetup.MetricData> | undefined
	> = {};

	resolveGenericMetricWithSpecific(
		genericMetricKey: string,
		spaceAccessId: number,
	): Promise<DSSetup.MetricSetupData> {
		const metric = this.getMetricForKeyAndSpaceAccessId(genericMetricKey, null);

		const forceWait = () => {
			// force waiting a bit so we refresh metrics
			return new Promise((resolve) => {
				later(resolve, null, 100);
			});
		};

		if (metric?.isGenericCustom) {
			// before we can change & save data-selects etc, we need to create the real metrics on BE side
			const payload = {
				metricKey: genericMetricKey,
				spaceAccessId,
			};

			const idForCache = `${payload.spaceAccessId}-${payload.metricKey}`;
			const cachedPromise = this._genericCustomResolveRequestMap[idForCache];
			if (cachedPromise) {
				return cachedPromise;
			} else {
				const promise = new Promise<DSSetup.MetricData>((resolve) => {
					this.requestService
						.api<APIData.Metric.RecordResponse>('POST', '/create-from-generic', payload)
						.then((reply) => {
							this.storeService.pushPayload(reply);
							const metricId = reply?.metric?.id;
							if (!metricId) {
								throw {
									code: 422,
									errors: [
										{
											code: 422,
											name: 'general',
											message: 'Metric was not created, try again later',
										},
									],
								};
							}
							void forceWait().then(() => {
								const metricAgain = this.findModel(String(metricId));
								resolve(
									metricAgain?.metricSetupData || {
										key: reply.metric.key,
										spaceAccessId,
									},
								);
							});
						})
						.catch((errorData) => {
							this.requestService.showAllValidationErrors(errorData);
							delete this._genericCustomResolveRequestMap[idForCache]; // make sure we request it again later
							// error, do not replace with new
							resolve(metric.metricSetupData!);
						});
				});
				this._genericCustomResolveRequestMap[idForCache] = promise;
				return promise;
			}
		} else {
			return forceWait().then(() => {
				const metricAgain = this.getMetricForKeyAndSpaceAccessId(
					genericMetricKey,
					spaceAccessId,
				);
				return metricAgain?.metricSetupData || { key: genericMetricKey, spaceAccessId };
			});
		}
	}

	getGenericMetricForConnectedMetric(connectedMetric: Metric) {
		return this.getGenericMetricsForSourceTypeKey(connectedMetric.dataSourceTypeKey).find(
			(m) => m.key === connectedMetric.key,
		);
	}

	// ----- Get dimension values for metrics ------
	private queriedDimensions: Record<string, DSSetup.DimensionItem[]> = {};
	private _metricDimensionsFetching: Record<string, Promise<DSSetup.DimensionItem[]>> = {};

	fetchDimensions(metricDataItems: DSSetup.MetricData[]): Promise<DSSetup.DimensionItem[]> {
		const uniqueKey = String(hashCode(JSON.stringify(metricDataItems)));

		let queried: DSSetup.DimensionItem[] | null = null;
		try {
			queried = this.queriedDimensions[uniqueKey] || null;
		} catch (_e) {
			// skip
		}

		let promise: Promise<DSSetup.DimensionItem[]>;
		if (queried) {
			promise = Promise.resolve(queried);
		} else if (typeof this._metricDimensionsFetching[uniqueKey] !== 'undefined') {
			promise = this._metricDimensionsFetching[uniqueKey]!;
		} else {
			// return Promise.resolve([
			// 	{ key: 'country', value: 'Country' },
			// 	{ key: 'device', value: 'Device' },
			// ]);
			promise = new Promise((resolve, reject) => {
				let url = '/metrics/dimensions';
				if (this.navigationService.isDatawallApp) {
					url = `/token/${this.requestService.wallToken}${url}`;
				}
				const data: DSSetup.MetricData[] = copyObject(metricDataItems);
				void this.requestService
					.api<DSSetup.DimensionItem[]>('POST', url, data, { timeout: 3 * 60 * 1000 })
					.then(
						(dimensions) => {
							// dummy data
							// dimensions = [{key: 'country', value: 'Country' }, {key: 'device', value: 'Device'}];

							this.queriedDimensions[uniqueKey] = dimensions;

							// invalidate in 1min
							later(() => {
								try {
									delete this.queriedDimensions[uniqueKey];
								} catch (_e) {
									// skip
								}
							}, 60000);

							resolve(dimensions);
						},
						(errorData: RequestError) => {
							reject(errorData);
							// throw errorData;
						},
					)
					.catch()
					.finally(() => {
						delete this._metricDimensionsFetching[uniqueKey];
					});
			});
		}

		return promise;
	}

	cleanupDimensionsCacheForMetricData(metricData: DSSetup.MetricData): void {
		const cacheKey = String(hashCode(JSON.stringify([metricData])));
		delete this.queriedDimensions[cacheKey];
	}

	loadMasterFilterDimensions(metricSetup: MetricSetupForMasterFilter): Promise<void> {
		// in case we are in-app and have metric models, read dimensions from them
		// else fetch dimensions from API
		// logged in (mobile) vs public token (datawall) endpoints are checked in fetchDimensions
		if (this.savedRecords.length) {
			const dimensions: DSSetup.DimensionItem[] = [];
			metricSetup._metricData.forEach((item) => {
				const metric = this.getMetricForKeyAndSpaceAccessId(item.key, item.spaceAccessId);
				(metric?.dimensions || []).forEach((dimensionData) => {
					if (!dimensions.find((d) => d.key === dimensionData.key)) {
						dimensions.push({
							key: dimensionData.key,
							value: dimensionData.value,
						});
					}
				});
			});

			this.setMasterFilterDimensions(metricSetup, dimensions);
			return Promise.resolve();
		}

		return this.fetchDimensions(metricSetup._metricData).then((list) => {
			this.setMasterFilterDimensions(metricSetup, list);
		});
	}

	private setMasterFilterDimensions(
		metricSetup: MetricSetupForMasterFilter,
		dimensionItems: DSSetup.DimensionItem[],
	) {
		const selected = metricSetup.dimensions;
		selected.forEach((dimension) => {
			if (!dimensionItems.find((i) => i.key === dimension)) {
				dimensionItems.push({
					key: dimension,
					value: dimension,
				});
			}
		});
		metricSetup._metricDimensions = dimensionItems;
	}

	// ----- Dimensions & units requests ------

	private queriedDimensionValues: Record<
		string,
		Record<string, APIData.Metrics.DimensionValuesResponse>
	> = {};
	private _metricDimensionValuesFetching: Record<
		string,
		Promise<APIData.Metrics.DimensionValuesResponse>
	> = {};

	private fetchDimensionValues(
		metricDataItems: DSSetup.MetricSetupWithoutFiltersData[],
		dateRange?: DateRange,
		context?: string,
	): Promise<APIData.Metrics.DimensionValuesResponse> {
		let formulaStr = 'null';
		if (dateRange && !dateRange.isEmpty) {
			const formula = dateRange.formula;
			formulaStr = JSON.stringify(formula);
		}

		// cache differently for a context
		// used for scripts context where qb metric dimensions are not renamed
		// https://app.asana.com/0/7582091858306/1199140976384302/f
		// Proper solution after which this can be removed: https://app.asana.com/0/1158681670738537/1199142408346533/f
		let uniqueKey = String(hashCode(JSON.stringify(metricDataItems)));
		if (context) {
			uniqueKey += '/' + context;
		}

		let queried: APIData.Metrics.DimensionValuesResponse | undefined;
		try {
			queried = this.queriedDimensionValues[uniqueKey]?.[formulaStr];
		} catch (_e) {
			// skip
		}

		let promise: Promise<APIData.Metrics.DimensionValuesResponse>;
		if (queried) {
			promise = Promise.resolve(queried);
		} else if (typeof this._metricDimensionValuesFetching[uniqueKey] !== 'undefined') {
			promise = this._metricDimensionValuesFetching[uniqueKey]!;
		} else {
			promise = new Promise((resolve, reject) => {
				const data: APIData.Metrics.DimensionValuesRequestItem[] =
					copyObject(metricDataItems);
				data.forEach((item) => {
					if (dateRange && !dateRange.isEmpty) {
						item.dateRange = dateRange.formula;
					}
					if (context) {
						item.context = context;
					}
				});

				let url = '/metrics/dimensions/values';
				if (this.navigationService.isDatawallApp) {
					url = `/token/${this.requestService.wallToken}${url}`;
				}

				void this.requestService
					.api<APIData.Metrics.DimensionValuesResponse>('POST', url, data, {
						timeout: 3 * 60 * 1000,
					})
					.then(
						(list) => {
							// dummy data
							// list = {dimension_values: ['Organic', 'Social', 'Paid', 'Other'], units:['$', 'EUR']};

							(this.queriedDimensionValues[uniqueKey] ??= {})[formulaStr] = list;

							// invalidate in 1min
							later(() => {
								try {
									delete this.queriedDimensionValues[uniqueKey]?.[formulaStr];
								} catch (_e) {
									// skip
								}
							}, 60000);

							resolve(list);
						},
						(errorData: RequestError) => {
							reject(errorData);
							// throw errorData;
						},
					)
					.catch()
					.finally(() => {
						delete this._metricDimensionValuesFetching[uniqueKey];
					});
			});
		}

		return promise;
	}

	cleanupDimensionValuesCacheForMetricData(metricData: DSSetup.MetricData): void {
		const cacheKey = String(hashCode(JSON.stringify([metricData])));
		delete this.queriedDimensionValues[cacheKey];
		delete this.queriedDimensionValues[cacheKey + '/script'];
	}

	getAttributes(
		metricDataItems: DSSetup.MetricSetupWithoutFiltersData[],
		dateRange?: DateRange,
		context?: string,
	): Promise<string[]> {
		return new Promise((resolve, reject) => {
			this.fetchDimensionValues(metricDataItems, dateRange, context).then(
				(list) => {
					resolve(list.dimension_values || []);
				},
				(errorData) => {
					// this.loggerService.logError('getAttributes failed', {code: errorData.code, metrickey, response: JSON.stringify(errorData.errors)});
					reject(errorData);
				},
			);
		});
	}

	getUnits(
		metricDataItems: DSSetup.MetricSetupWithoutFiltersData[],
		dateRange?: DateRange,
		context?: string,
	): Promise<string[]> {
		return new Promise((resolve, reject) => {
			this.fetchDimensionValues(metricDataItems, dateRange, context).then(
				(list) => {
					resolve(list.units || []);
				},
				(errorData) => {
					// this.loggerService.logError('getUnits failed', {code: errorData.code, metrickey, response: JSON.stringify(errorData.errors)});
					reject(errorData);
				},
			);
		});
	}

	getOptimalUnit(
		metricData: DSSetup.MetricData,
		dateRange?: DateRange,
		context?: string,
		allowAllUnits = false,
	): Promise<string> {
		return new Promise((resolve, reject) => {
			this.getUnits([metricData], dateRange, context)
				.then((list) => {
					if (!list) {
						throw 'No units list provided!';
					}

					let unit;

					if (!list.length) {
						unit = allowAllUnits ? Unit.ALL : Unit.EMPTY;
					} else {
						unit = allowAllUnits && list.length > 1 ? Unit.ALL : list[0]!;
					}

					resolve(unit);
				})
				.catch(reject);
		});
	}

	// ------ Metric limitations ------

	metricLimitations: Record<string, APIData.Metrics.LimitationsResponse> = {};
	_metricLimitationsFetchingPromises: Record<
		string,
		Promise<APIData.Metrics.LimitationsResponse>
	> = {};

	getMetricLimitations(metricData: DSSetup.MetricData[] = []) {
		let promise: Promise<APIData.Metrics.LimitationsResponse>;

		const cacheKey = JSON.stringify(metricData);

		if (this.metricLimitations[cacheKey]) {
			promise = Promise.resolve(this.metricLimitations[cacheKey]);
		} else if (typeof this._metricLimitationsFetchingPromises[cacheKey] !== 'undefined') {
			promise = this._metricLimitationsFetchingPromises[cacheKey]!;
		} else if (this.navigationService.isLoadedFromSnapshooter) {
			// do not make these requests when snapshooting
			promise = Promise.reject(null);
		} else {
			promise = new Promise((resolve, reject) => {
				const payload = metricData,
					invalidateIn = (metricData.length ? 5 : 30) * 60 * 1000; // 5min for metrics, 30min for general acc stuff
				this.requestService
					.api<APIData.Metrics.LimitationsResponse>(
						'POST',
						`/metrics/limitations`,
						payload,
					)
					.then(
						(reply) => {
							this.metricLimitations[cacheKey] = reply;
							resolve(reply);
							later(() => {
								this.cleanupLimitationsCacheForKey(cacheKey);
							}, invalidateIn);
						},
						(errorData) => {
							reject(errorData);
							// throw errorData;
						},
					)
					.catch(() => {
						/* skip */
					})
					.finally(() => {
						delete this._metricLimitationsFetchingPromises[cacheKey];
					});
			});
			this._metricLimitationsFetchingPromises[cacheKey] = promise;
		}

		return promise;
	}

	cleanupLimitationsCacheForKey(mk: string): void {
		delete this.metricLimitations[mk];
	}

	purgeLimitationsCache() {
		this.metricLimitations = {};
	}

	getFormulaWithOptimalValidGranulation(
		props: {
			metricData?: DSSetup.MetricData;
			dateRange?: DateRangeInterface.FormulaInterface;
		},
		isNumber?: boolean,
	): Promise<DateRangeInterface.FormulaInterface> {
		return new Promise((resolve, reject) => {
			if (props.metricData && props.dateRange) {
				const limitations = new MetricLimitations({
					isNumber: !!isNumber,
					metrics: [props.metricData],
					dateRange: DateRange.createFromFormula({ range: props.dateRange.range }),
				});

				limitations.limitationsLoaded
					?.then(() => {
						limitations.selectOptimalValidGranulation();
						const formula = limitations.dateRange?.formula;

						if (formula?.granulation) {
							resolve(formula);
						} else {
							reject();
						}
					})
					.catch(reject);
			} else {
				reject();
			}
		});
	}

	// ------ Metric operator limitations ------
	@tracked operatorLimitations: Record<string, MetricFilterOperator[]> = {};
	_operatorLimitationsFetchingPromises: Record<string, Promise<MetricFilterOperator[]>> = {};

	getMetricOperatorLimitations(metricDataArray: DSSetup.MetricSetupWithoutFiltersData[] = []) {
		let promise: RSPromise<MetricFilterOperator[]>;

		const allOperators = this.constantsService.metricFilterOperators.map(
			(i) => i.value as MetricFilterOperator,
		);

		const cacheKey = stringifyAlphabetical(metricDataArray);

		if (this.operatorLimitations[cacheKey]) {
			promise = Promise.resolve(this.operatorLimitations[cacheKey]);
		} else if (typeof this._operatorLimitationsFetchingPromises[cacheKey] !== 'undefined') {
			promise = this._operatorLimitationsFetchingPromises[cacheKey]!;
		} else if (this.navigationService.isLoadedFromSnapshooter) {
			// do not make these requests when snapshooting
			promise = Promise.resolve(allOperators);
		} else {
			const payload: APIData.Metrics.FilterOperatorLimitations.Request = metricDataArray;

			let requestPromise:
				Promise<APIData.Metrics.FilterOperatorLimitations.Response> | undefined;
			promise = new Promise<MetricFilterOperator[]>((resolve) => {
				// don't invalidate
				requestPromise = this.requestService.api(
					'POST',
					'/metrics/filter-limitations',
					payload,
				);

				requestPromise
					.then((reply) => {
						return reply.operators;
					})
					.then((operators: MetricFilterOperator[]) => {
						this.operatorLimitations[cacheKey] = operators;

						this.operatorLimitations = this.operatorLimitations;

						resolve(operators);
					})
					.catch(() => {
						resolve(allOperators);
					})
					.finally(() => {
						delete this._operatorLimitationsFetchingPromises[cacheKey];
					});
			});
			promise.jqPromise = requestPromise;
			this._operatorLimitationsFetchingPromises[cacheKey] = promise;
		}

		return promise;
	}

	getMetricOperatorLimitationsForDimension(
		metricDataArray: DSSetup.MetricData[],
		dimension: string,
	) {
		// just adding the same dimension to all metric datas
		const metricSetupDataArray = metricDataArray.map((metricData) => {
			const metricSetupData: DSSetup.MetricSetupWithoutFiltersData = Object.assign(
				{},
				metricData,
			);
			metricSetupData.dimensions = [dimension];
			return metricSetupData;
		});

		return this.getMetricOperatorLimitations(metricSetupDataArray);
	}

	getCachedOperatorLimitations(
		metricSetupData: DSSetup.MetricSetupWithoutFiltersData[] = [],
	): MetricFilterOperator[] | undefined {
		// does not request!
		const cacheKey = stringifyAlphabetical(metricSetupData);
		return this.operatorLimitations[cacheKey];
	}

	// ------ General Requests ------

	get isMetricsRequested() {
		return !!this._metricsRequested;
	}

	@tracked protected _metricsRequested = false;
	protected _metricsRequestPromise: Promise<void> | null = null;
	requestMetrics(): Promise<void> {
		const variablePrefix = 'metrics';
		return this.requestService.dataRequestOnce<typeof variablePrefix>(
			this,
			variablePrefix,
			() => {
				// const fakeData = window.dbx_mock['GET /d/metrics'];
				// const promise = Promise.resolve(fakeData);

				const promise1 = this.requestService.storeFindAll(ModelName.METRIC);
				const promise2 = this.mmSourceTypesService.requestSourceTypes();

				return Promise.allSettled([promise1, promise2]).then(() => {
					return new Promise((resolve) => {
						next(() => {
							this.updateMetricsFromSources(true);
							next(() => {
								this.fetchAndUpgradeMetricVerifications()
									.catch(() => undefined)
									.finally(() => resolve({}));
							});
						});
					});
				});
			},
		);
	}

	prepareForMetricEditing() {
		// need to request scripts, customqueries, goals and horizon metrics or we won't see options to edit
		return Promise.all([
			this.requestMetrics(),
			this.mmCustomQueriesService.requestCustomQueries(),
			this.mmScriptsService.requestScripts(),
			this.mmGoalsService.requestSpaceGoals(),
			this.mmHorizonMetricsService.requestHorizonMetrics(),
		]);
	}

	// ------ For-store ------

	modelName = ModelName.METRIC;

	createModel(model: MetricRecord): Metric {
		return new Metric({ model });
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/metrics': ModelManagerMetricsService;
	}
}
